import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Loader2, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Name input with personnel autocomplete.
 *
 * Props:
 *  - value (string)                — current name text
 *  - onChange (name: string)       — fires on every keystroke
 *  - onSelect ({name, email, discipline, id}) — fires when a personnel row is picked;
 *      parent should use this to auto-fill email (and optionally role).
 *  - placeholder (string)
 *  - disabled (boolean)
 *  - required (boolean)
 *  - testId (string)
 */
export default function PersonnelNameAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Name *',
  disabled = false,
  required = false,
  testId = 'personnel-name-input',
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  // Tracks the most recent text the user typed (vs. text we set after a pick),
  // so we don't immediately re-query after a selection.
  const lastSelectedRef = useRef('');
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/personnel/search`, {
        params: { q, limit: 8 },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setResults(Array.isArray(data) ? data : []);
    } catch (_) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (disabled) return;
    const q = (value || '').trim();
    if (q.length < 2 || q === lastSelectedRef.current) {
      setResults([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(q);
      setOpen(true);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, disabled, runSearch]);

  // Click-outside dismiss
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setHighlight(-1);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (p) => {
    const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim();
    const email = p.work_email || p.email || '';
    lastSelectedRef.current = fullName;
    setOpen(false);
    setResults([]);
    setHighlight(-1);
    if (onSelect) {
      onSelect({
        id: p.id,
        name: fullName,
        email,
        discipline: p.discipline || '',
      });
    } else if (onChange) {
      onChange(fullName);
    }
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      pick(results[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          lastSelectedRef.current = '';
          onChange?.(e.target.value);
        }}
        onFocus={() => {
          if ((value || '').trim().length >= 2 && results.length > 0) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete="off"
        data-testid={testId}
      />
      {open && (results.length > 0 || loading) && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-auto"
          data-testid={`${testId}-dropdown`}
        >
          {loading && results.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> Searching…
            </div>
          )}
          {results.map((p, idx) => {
            const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || '(no name)';
            const email = p.work_email || p.email || '';
            const active = idx === highlight;
            return (
              <button
                type="button"
                key={p.id || idx}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(p);
                }}
                onMouseEnter={() => setHighlight(idx)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                  active ? 'bg-primary/10' : 'hover:bg-muted'
                }`}
                data-testid={`${testId}-option-${idx}`}
              >
                <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{fullName}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {email || 'No email on file'}
                    {p.discipline ? ` • ${p.discipline}` : ''}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
